/**
 * =============================================================================
 * Hook: useReferralCreditHistory
 * Versão: 1.0.0 (2026-05-04)
 *
 * Descrição:
 *   Carrega o histórico paginado de movimentações de créditos de conversão do
 *   tenant ativo via RPC `get_referral_credit_history`. Acesso restrito a
 *   admin do tenant (validado no backend).
 *
 *   Filtros:
 *     - reason: 'consume' | 'reverse' | 'grant' | null (todos)
 *     - period_start / period_end (TIMESTAMPTZ)
 *     - limit / offset (paginação)
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useReferralCreditHistory');

export type CreditReason =
  | 'consume'
  | 'reverse'
  | 'grant'
  | 'free_tier_grant'
  | 'bonus'
  | 'bonus_tier_activation'
  | 'purchase';

export interface CreditHistoryRow {
  id_short: string;
  reason: CreditReason | string;
  delta: number;
  balance_after: number;
  unbilled: boolean;
  created_at: string;
  referral: { id_short: string; lead_name: string | null; status: string } | null;
  commission: {
    id_short: string;
    status: string;
    reward_value: number | null;
    currency: string | null;
  } | null;
  deal: { id: string; id_short: string; title: string | null; value: number | null } | null;
  affiliate: { id_short: string; full_name: string | null } | null;
}

export interface CreditHistoryResult {
  total: number;
  rows: CreditHistoryRow[];
}

export interface UseReferralCreditHistoryParams {
  limit?: number;
  offset?: number;
  reason?: CreditReason | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  enabled?: boolean;
}

export function useReferralCreditHistory({
  limit = 50,
  offset = 0,
  reason = null,
  periodStart = null,
  periodEnd = null,
  enabled = true,
}: UseReferralCreditHistoryParams = {}) {
  return useQuery({
    queryKey: ['referral-credit-history', limit, offset, reason, periodStart, periodEnd],
    queryFn: async (): Promise<CreditHistoryResult> => {
      const { data, error } = await supabase.rpc('get_referral_credit_history', {
        p_limit: limit,
        p_offset: offset,
        p_reason: reason,
        p_period_start: periodStart,
        p_period_end: periodEnd,
      });

      if (error) {
        log.error('Falha ao carregar histórico de créditos', { error: error.message });
        throw error;
      }

      return (data ?? { total: 0, rows: [] }) as unknown as CreditHistoryResult;
    },
    enabled,
    staleTime: 30_000,
  });
}
